import { extractTranslations } from '@assets/i18n/config';
import { BoundObjectType } from '@generated/graphql';
import type { TranslatedString } from '@interfaces/shared';
import {
  type HouseholdIpsValues,
  IPS_NARRATIVE_FIELDS,
} from '../householdIpsComparison/householdIpsFields';

type SetupEntityBoundModelTargetAllocation = {
  secondaryAssetClassId: string;
  min: number;
  max: number;
};

/**
 * The household-only slice of the generated `SetupEntityBoundModelInput`: the
 * fields this flow sends, with the ones it always sends required and the
 * translated copy in the shape the read queries hand back.
 */
export type SetupEntityBoundModelInput = {
  baseModelPortfolioId: string;
  boundObjectType: BoundObjectType.Household;
  boundObjectId: string;
  organizationId: string;
  replaceExistingBase?: boolean;
  translatedPortfolioDescription?: TranslatedString;
  translatedInvestmentObjective?: TranslatedString;
  translatedLiquidity?: TranslatedString;
  translatedPortfolioDisclaimer?: TranslatedString;
  targetAllocations?: SetupEntityBoundModelTargetAllocation[];
};

export type BuildSetupEntityBoundModelInputArgs = {
  baseModelPortfolioId: string;
  boundObjectId: string;
  organizationId: string;
  /** The IPS to apply to the household's copy. Omit to inherit the base model. */
  ips?: HouseholdIpsValues;
  /**
   * Lets product-service retire a copy the household holds of a *different*
   * base model and take a fresh one. A copy of the base model being sent is
   * reused untouched either way, so this only matters when the household is
   * being moved onto another model.
   */
  replaceExistingBase?: boolean;
};

/**
 * A band names its asset class by id, and a row left without one is a row the
 * advisor added and never filled in.
 */
const toTargetAllocations = (
  values: HouseholdIpsValues,
): SetupEntityBoundModelTargetAllocation[] | undefined =>
  values.targetAllocations
    ?.filter((allocation) => !!allocation.secondaryAssetClass?.id)
    .map((allocation) => ({
      secondaryAssetClassId: allocation.secondaryAssetClass?.id as string,
      min: allocation.min,
      max: allocation.max,
    }));

/**
 * Translated copy comes back from the read query carrying its `__typename`,
 * which the input type has no field for. A field the model never had is left
 * out rather than sent empty, so the copy is inherited from the base model
 * instead of being blanked.
 */
const toIpsOverrides = (
  ips: HouseholdIpsValues,
): Partial<SetupEntityBoundModelInput> => {
  const overrides: Partial<SetupEntityBoundModelInput> = {};

  for (const { valueKey } of IPS_NARRATIVE_FIELDS) {
    const value = ips[valueKey];
    if (value) overrides[valueKey] = extractTranslations(value);
  }

  const targetAllocations = toTargetAllocations(ips);
  if (targetAllocations) overrides.targetAllocations = targetAllocations;

  return overrides;
};

/**
 * Builds `SetupEntityBoundModelInput` for the household flows that commit to
 * product-service.
 *
 * Initialize sends only the bind (base model + household + org): it has no
 * per-section editing, so the copy must faithfully inherit the base model. The
 * IPS recap sends the edited statement alongside, which product-service applies
 * to the copy it creates or reuses.
 */
export const buildSetupEntityBoundModelInput = ({
  baseModelPortfolioId,
  boundObjectId,
  organizationId,
  ips,
  replaceExistingBase,
}: BuildSetupEntityBoundModelInputArgs): SetupEntityBoundModelInput => ({
  baseModelPortfolioId,
  boundObjectType: BoundObjectType.Household,
  boundObjectId,
  organizationId,
  ...(replaceExistingBase ? { replaceExistingBase } : {}),
  ...(ips ? toIpsOverrides(ips) : {}),
});
